import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { AddAssignmentRole, AssignmentRoleLoad, DeleteAssignmentRole, ProjectsLoad, UpdateAssignmentRole } from '@completion/actions';
import { AssignmentRole, Confirmation } from '@completion/models';
import { getAssignmentRoles, getCurrentProject } from '@completion/selectors';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog';
import {
  AssignmentRoleEditDialogComponent,
  AssignmentRoleEditDialogData,
} from './assignment-role-edit-dialog/assignment-role-edit-dialog.component';

@Component({
  selector: 'app-assignment-role',
  templateUrl: './assignment-role.component.html',
  styleUrls: ['./assignment-role.component.scss'],
  standalone: false,
})
export class AssignmentRoleComponent implements OnInit, OnDestroy {
  displayedColumns = ['name', 'description', 'sharedEmail', 'type', 'sendToAll', 'edit', 'delete'];
  dataSource: AssignmentRole[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new AssignmentRoleLoad());
        } else {
          this.store.dispatch(new ProjectsLoad());
        }
      });

    this.store
      .select(getAssignmentRoles)
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.dataSource = roles || [];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Compute the next ordinal based on existing roles
  private getNextOrdinal(): number {
    if (!this.dataSource || this.dataSource.length === 0) {
      return 0;
    }
    const ordinals = this.dataSource
      .map((r) => (typeof r.ordinal === 'number' ? r.ordinal : null))
      .filter((v): v is number => v !== null && !isNaN(v));
    if (ordinals.length === 0) {
      return 0;
    }
    return Math.max(...ordinals) + 1;
  }

  addAssignmentRole(): void {
    const empty: AssignmentRole = {
      name: '',
      description: '',
      sharedEmail: '',
      type: '',
      sendToAll: false,
    };
    this.openEditDialog(empty, (result) => {
      if (result) {
        // Assign ordinal for new role: last ordinal + 1 (or 0 if none yet)
        const nextOrdinal = this.getNextOrdinal();
        const roleToAdd: AssignmentRole = { ...result, ordinal: nextOrdinal };
        this.store.dispatch(new AddAssignmentRole(roleToAdd));
      }
    });
  }

  editAssignmentRole(role: AssignmentRole): void {
    this.openEditDialog(role, (result) => {
      if (result) {
        this.store.dispatch(new UpdateAssignmentRole(result));
      }
    });
  }

  deleteAssignmentRole(role: AssignmentRole): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Assignment Role',
          message: `Are you sure you want to delete ${role.name}?`,
          confirmationTitle: 'Delete',
          cancelTitle: 'Cancel',
          isCancelDisplay: true,
        } as Confirmation,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new DeleteAssignmentRole(role));
        }
      });
  }

  private openEditDialog(role: AssignmentRole, onSave: (updated: AssignmentRole | null) => void): void {
    const ref = this.dialog.open<AssignmentRoleEditDialogComponent, AssignmentRoleEditDialogData, AssignmentRole | null>(
      AssignmentRoleEditDialogComponent,
      {
        width: '640px',
        maxWidth: '90vw',
        data: { role },
      },
    );

    ref.afterClosed().subscribe((result) => {
      onSave(result || null);
    });
  }
}
